import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Section } from "../components/Common";
import PractitionerRecommendations from "../components/PractitionerRecommendations";
import { ArrowRight, Building2, Tag, Briefcase, BookOpen, BadgePercent, UserPlus, PenSquare, Megaphone, Users2 } from "lucide-react";
import AppCta from "../components/AppCta";

/**
 * Business & Corporate hub — gateway page for the business branch of the IA.
 */
export default function BusinessOverviewPage() {
  const { t } = useTranslation();
  const cards = [
    {
      to: "/business/partners", title: t("nav.biz_partners"),
      blurb: t("business_overview.cards.partners_blurb"),
      image: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=900&q=80",
      icon: Briefcase,
    },
    {
      to: "/business/corporate", title: t("nav.biz_corporate"),
      blurb: t("business_overview.cards.corporate_blurb"),
      image: "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?auto=format&fit=crop&w=900&q=80",
      icon: Building2,
    },
    {
      to: "/business/offers", title: t("nav.biz_offers"),
      blurb: t("business_overview.cards.offers_blurb"),
      image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&w=900&q=80",
      icon: BadgePercent,
    },
    {
      to: "/marketplace", title: t("nav.biz_marketplace"),
      blurb: t("business_overview.cards.marketplace_blurb"),
      image: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=900&q=80",
      icon: Tag,
    },
    {
      to: "/business/directory", title: t("nav.biz_directory"),
      blurb: t("business_overview.cards.directory_blurb"),
      image: "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=900&q=80",
      icon: BookOpen,
    },
  ];
  return (
    <div data-testid="page-business-overview">
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&w=1800&q=80"
            alt=""
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/65 to-black/30" />
        </div>
        <div className="relative app-container py-20 md:py-28">
          <div className="max-w-2xl">
            <div className="eyebrow mb-3" data-testid="business-overview-eyebrow">{t("business_overview.eyebrow")}</div>
            <h1 className="font-display text-4xl md:text-5xl leading-tight max-w-[22ch]" data-testid="business-overview-title">{t("business_overview.title")}</h1>
            <p className="text-white/80 mt-5 leading-relaxed max-w-[60ch] body-copy text-lg">
              {t("business_overview.subtitle")}
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/marketplace/register" className="btn-primary text-sm inline-flex items-center gap-2" data-testid="business-overview-cta-list">
                {t("business_overview.cta_list")} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/marketplace" className="btn-ghost text-sm" data-testid="business-overview-cta-browse">{t("business_overview.cta_browse")}</Link>
            </div>
          </div>
        </div>
      </section>

      <Section eyebrow={t("business_overview.sections_eyebrow")} title={t("business_overview.sections_title")} subtitle={t("business_overview.sections_subtitle")}>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="business-overview-grid">
          {cards.map((c) => {
            const Icon = c.icon;
            return (
              <Link
                key={c.to}
                to={c.to}
                className="card-surface overflow-hidden group flex flex-col hover:border-[#C8A24A]/40 transition-colors"
                data-testid={`business-card-${c.to.split("/").pop()}`}
              >
                <div className="aspect-[16/10] overflow-hidden relative">
                  <img src={c.image} alt={c.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                  <div className="absolute bottom-3 left-3 right-3 flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-black/55 border border-[#C8A24A]/45 backdrop-blur grid place-items-center">
                      <Icon className="w-4 h-4 gold-text" />
                    </div>
                    <div className="font-display text-lg leading-snug">{c.title}</div>
                  </div>
                </div>
                <div className="p-4 flex-1 flex flex-col">
                  <p className="text-sm text-white/70 leading-relaxed flex-1">{c.blurb}</p>
                  <div className="mt-3 text-xs gold-text inline-flex items-center gap-1">
                    {t("ecosystem.explore")} <ArrowRight className="w-3 h-3" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </Section>

      <PractitionerRecommendations
        eyebrow={t("business_overview.recs_eyebrow")}
        title={t("business_overview.recs_title")}
        subtitle={t("business_overview.recs_subtitle")}
        topic="corporate"
        limit={3}
        testid="business-overview-recs"
      />

      {/* Phase 10 — Ecosystem journey for businesses */}
      <BusinessJourney />

      <AppCta variant="business" testid="business-app-cta" />
    </div>
  );
}

/* ================================================================ */
/* Phase 10 — Business Ecosystem Journey                              */
/* Editorial 4-step pathway: Discover → Become a member →             */
/* Create profile → Advertise → Reach users.                          */
/* No new backend — reuses /signup, /marketplace/register and the     */
/* /api/shop/waitlist we already built (kind=business_advertise).     */
/* ================================================================ */
function BusinessJourney() {
  const { t } = useTranslation();
  const steps = [
    {
      icon: UserPlus,
      key: "join",
      title: t("business_journey.join_title", "1. Become a member"),
      body: t(
        "business_journey.join_body",
        "Create your DONiTSH!T account and let us know you're representing a business. Membership unlocks the marketplace, offers and ecosystem visibility."
      ),
      cta_to: "/signup?intent=business",
      cta_label: t("business_journey.join_cta", "Create an account"),
      testid: "biz-journey-join",
    },
    {
      icon: PenSquare,
      key: "profile",
      title: t("business_journey.profile_title", "2. Create your business profile"),
      body: t(
        "business_journey.profile_body",
        "Submit your business — services, languages, locations and imagery. Our team reviews every listing to keep the marketplace curated and premium."
      ),
      cta_to: "/marketplace/register",
      cta_label: t("business_journey.profile_cta", "Submit business profile"),
      testid: "biz-journey-profile",
    },
    {
      icon: Megaphone,
      key: "advertise",
      title: t("business_journey.advertise_title", "3. Advertise inside the ecosystem"),
      body: t(
        "business_journey.advertise_body",
        "Featured placements, member offers and workshop promotions land inside the DONiTSH!T ecosystem. Join the waitlist below to be first in line when advertising opens."
      ),
      // Uses the same waitlist endpoint as Shop coming-soon tiles.
      // We route to a lightweight inline waitlist form (see BusinessAdvertiseWaitlist).
      cta_to: null,
      testid: "biz-journey-advertise",
      component: "advertise_waitlist",
    },
    {
      icon: Users2,
      key: "reach",
      title: t("business_journey.reach_title", "4. Reach DONiTSH!T users"),
      body: t(
        "business_journey.reach_body",
        "Members discover your business through curated recommendations, marketplace search and in-app suggestions — grounded in the DONiTSH!T Dosha framework."
      ),
      cta_to: "/marketplace",
      cta_label: t("business_journey.reach_cta", "See how it looks today"),
      testid: "biz-journey-reach",
    },
  ];

  return (
    <Section
      eyebrow={t("business_journey.eyebrow", "— Your business, in our ecosystem")}
      title={t("business_journey.title", "A clear pathway from discovery to reach.")}
      subtitle={t(
        "business_journey.subtitle",
        "How independent practitioners, wellness brands and corporate partners join, list and grow with DONiTSH!T."
      )}
    >
      <div
        className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4"
        data-testid="business-journey-grid"
      >
        {steps.map((s, i) => {
          const Icon = s.icon;
          return (
            <div
              key={s.key}
              className="card-surface p-5 flex flex-col gap-3 relative overflow-hidden"
              data-testid={s.testid}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full border border-[#C8A24A]/45 bg-gradient-to-br from-[#E2C77A]/25 to-[#7A5F2A]/15 grid place-items-center">
                  <Icon className="w-4 h-4 gold-text" />
                </div>
                <div className="text-[10px] tracking-[0.28em] uppercase gold-text">
                  {t("business_journey.step_label", "Step {{n}}", { n: i + 1 })}
                </div>
              </div>
              <div className="font-display text-lg leading-snug">{s.title}</div>
              <p className="text-sm text-white/70 leading-relaxed flex-1">{s.body}</p>

              {s.component === "advertise_waitlist" ? (
                <BusinessAdvertiseWaitlist />
              ) : (
                <Link
                  to={s.cta_to}
                  className="btn-secondary text-sm inline-flex items-center gap-2 self-start"
                  data-testid={`${s.testid}-cta`}
                >
                  {s.cta_label}
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              )}
            </div>
          );
        })}
      </div>
    </Section>
  );
}

// Compact inline waitlist form for the "Advertise" step.
// Reuses POST /api/shop/waitlist (top_level=business, category=advertise).
function BusinessAdvertiseWaitlist() {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const [sent, setSent] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSending(true);
    try {
      await import("../lib/api").then(({ default: API }) =>
        API.post("/shop/waitlist", {
          email,
          category: "advertise",
          top_level: "business",
          language: i18n.language,
        })
      );
      setSent(true);
    } catch {
      // silent fail — user can retry
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div
        className="text-xs text-white/70 pt-1"
        data-testid="biz-journey-advertise-sent"
      >
        {t(
          "business_journey.advertise_sent",
          "You're on the list — we'll be in touch when advertising opens."
        )}
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="flex flex-col sm:flex-row gap-2 pt-1"
      data-testid="biz-journey-advertise-form"
    >
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder={t("shop.waitlist_email_ph", "your@email.com")}
        className="bg-[#0F1116] border border-white/10 rounded-lg px-3 py-2 text-sm flex-1 min-w-0"
        data-testid="biz-advertise-email"
      />
      <button
        type="submit"
        disabled={sending}
        className="btn-secondary text-sm inline-flex items-center gap-2 whitespace-nowrap disabled:opacity-40"
        data-testid="biz-advertise-submit"
      >
        {sending
          ? "\u2026"
          : t("business_journey.advertise_cta", "Join the waitlist")}
      </button>
    </form>
  );
}
